/**
 * net/replay.js — other players' throws, on the lane next to yours (GDD 10.4).
 *
 * This is the module the griefing-prevention design lives or dies on, so the
 * rule is stated once and obeyed absolutely: **a remote throw never becomes a
 * physics body.** It is a closed-form ballistic arc — `p + v·t − ½·g·s·t²` —
 * evaluated per frame and written straight to a pooled mesh's transform. There
 * is no Rapier call anywhere in this file. The `REMOTE_COSMETIC` collision
 * group exists to describe exactly this, and the strongest possible way to
 * honour "collides with nothing" is to never create the collider.
 *
 * What the viewer sees is a projectile arcing into the booth two metres to the
 * side, landing with a puff and a scatter of debris, thrown by a goose standing
 * at that lane with their name over its head. What the simulation sees is
 * nothing at all.
 *
 * @module net/replay
 */

import * as THREE from 'three';
import { COLLISION, DEFAULT_GRAVITY, NET, STALL_BY_ID, stallPlayOrigin } from '../constants.js';
import * as factory from '../assets3d/factory.js';

/**
 * The collision group a remote throw would carry if it had a body. It does not
 * have a body. Kept referenced so a search for REMOTE_COSMETIC lands here, on
 * the explanation, rather than only in the constants table.
 * @see ARCHITECTURE.md §6
 */
export const REMOTE_GROUP = COLLISION.REMOTE_COSMETIC;

/**
 * Position of a replayed projectile at time `t`. Pure, and the one piece of
 * maths in this module worth asserting directly (`__cc.net.arcAt`).
 *
 * @param {number[]} origin [x, y, z] release point, world space.
 * @param {number[]} velocity [x, y, z] release velocity, m/s.
 * @param {number} gravityScale Per-stall arc feel (GDD 5.4).
 * @param {number} t Seconds since release.
 * @param {THREE.Vector3} [out]
 * @returns {THREE.Vector3}
 */
export function arcPosition(origin, velocity, gravityScale, t, out = new THREE.Vector3()) {
  const g = DEFAULT_GRAVITY * gravityScale;
  return out.set(
    origin[0] + velocity[0] * t,
    origin[1] + velocity[1] * t - 0.5 * g * t * t,
    origin[2] + velocity[2] * t,
  );
}

/**
 * Where a peer stands, and where their throws land, when they are playing the
 * same stall as you.
 *
 * Lanes alternate side to side by peer index: 0 → +2 m, 1 → −2 m, 2 → +4 m, and
 * so on, in the stall's LOCAL X. Local +Z points into the booth, so local ±X is
 * "along the counter", which is where a neighbouring lane actually is.
 *
 * @param {string} stallId
 * @param {number} laneIndex
 * @returns {{ position: THREE.Vector3, yaw: number, offset: THREE.Vector3 }|null}
 */
export function laneAnchor(stallId, laneIndex = 0) {
  const spec = STALL_BY_ID[stallId];
  if (!spec) return null;

  const origin = stallPlayOrigin(spec);
  const sign = laneIndex % 2 === 0 ? 1 : -1;
  const rank = Math.floor(laneIndex / 2) + 1;
  const distance = NET.laneOffset * rank * sign;

  // Local +X of a node rotated by yaw θ about Y is (cos θ, 0, −sin θ).
  const offset = new THREE.Vector3(
    Math.cos(origin.yaw) * distance,
    0,
    -Math.sin(origin.yaw) * distance,
  );

  return {
    position: new THREE.Vector3(origin.x + offset.x, 0, origin.z + offset.z),
    yaw: origin.yaw,
    offset,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Projectile meshes
// ─────────────────────────────────────────────────────────────────────────────

/** What each stall's throwable looks like, when the handle does not say. */
const KIND_BY_STALL = Object.freeze({
  'tin-can-alley': 'baseball',
  'coconut-shy': 'heavy',
  'ring-toss': 'ring',
  'ping-pong-goldfish': 'pingpong',
  'basketball-shoot': 'basketball',
  'skee-ball': 'skee',
  'dart-balloons': 'dart',
});

/**
 * Decide what kind of object a throw was, for the receiver to draw.
 * @param {string} stallId
 * @param {object} [handle] The thrown body handle, if one is to hand.
 * @returns {string}
 */
export function throwKindFor(stallId, handle) {
  return handle?.userData?.kind || KIND_BY_STALL[stallId] || 'baseball';
}

/**
 * Build a cosmetic stand-in for a thrown object.
 *
 * The factory is asked first so a replayed baseball is the same baseball the
 * local player throws. An unknown kind from the wire falls back to a plain
 * sphere rather than throwing — the protocol already sanitised the string, but
 * a valid string is not the same as a supported one.
 *
 * @param {string} kind
 * @returns {THREE.Object3D}
 */
function makeProjectile(kind) {
  try {
    if (kind === 'coconut') return factory.createCoconut();
    if (kind === 'ring') return factory.createRing();
    if (kind === 'dart') return factory.createDart();
    if (['baseball', 'heavy', 'pingpong', 'basketball', 'skee'].includes(kind)) {
      return factory.createBall(kind);
    }
    return factory.createBall('baseball');
  } catch {
    return new THREE.Mesh(
      new THREE.SphereGeometry(0.04, 8, 6),
      factory.mat(0xc8c8c8, { rough: 0.7 }),
    );
  }
}

/** Debris and puff share one geometry each; there is no reason not to. */
let puffGeo = null;
let debrisGeo = null;

function ensureEffectGeometry() {
  if (puffGeo) return;
  puffGeo = new THREE.SphereGeometry(0.12, 8, 6);
  debrisGeo = new THREE.BoxGeometry(0.05, 0.05, 0.05);
}

/** One in-flight (or landing) replay. */
class ReplayShot {
  /**
   * @param {THREE.Object3D} mesh
   * @param {{ peerId:string, stallId:string, origin:number[], velocity:number[],
   *           gravityScale:number, kind:string }} data
   */
  constructor(mesh, data) {
    this.mesh = mesh;
    this.peerId = data.peerId;
    this.stallId = data.stallId;
    this.kind = data.kind;
    this.origin = data.origin;
    this.velocity = data.velocity;
    this.gravityScale = data.gravityScale;

    this.elapsed = 0;
    this.landed = false;
    this.landTimer = 0;
    /** @type {THREE.Mesh[]} */
    this.effects = [];
    this._spin = new THREE.Vector3(
      (this.velocity[2] || 1) * 0.6,
      0.8,
      -(this.velocity[0] || 1) * 0.6,
    );
  }
}

/**
 * Neighbour-lane replay renderer.
 *
 * Meshes are pooled per kind and the number of simultaneous shots is capped, so
 * a peer spamming throws costs a bounded amount of memory and frame time and
 * cannot be used as a denial-of-service against the player's own round.
 */
class ReplaySystem {
  constructor() {
    /** @type {THREE.Group|null} */ this.root = null;
    /** @type {ReplayShot[]} */ this.active = [];
    /** @type {Map<string, THREE.Object3D[]>} */ this.pool = new Map();
    /** Lifetime count, for tests and the badge. */ this.played = 0;
    this._scratch = new THREE.Vector3();
  }

  /** @param {THREE.Scene} scene */
  install(scene) {
    if (this.root) return this;
    this.root = new THREE.Group();
    this.root.name = 'net-replays';
    scene.add(this.root);
    return this;
  }

  /**
   * Render one remote throw.
   *
   * @param {{ peerId?:string, stallId:string, origin:number[], velocity:number[],
   *           gravityScale?:number, kind?:string, laneIndex?:number }} data
   * @returns {boolean} False if the stall is unknown or the system is not live.
   */
  spawn(data) {
    if (!this.root) return false;
    const anchor = laneAnchor(data.stallId, data.laneIndex ?? 0);
    if (!anchor) return false;

    if (this.active.length >= NET.maxReplayMeshes) this._retire(0);

    const kind = data.kind || 'baseball';
    const mesh = this._acquire(kind);
    const origin = [
      data.origin[0] + anchor.offset.x,
      data.origin[1],
      data.origin[2] + anchor.offset.z,
    ];

    mesh.position.set(origin[0], origin[1], origin[2]);
    mesh.visible = true;
    this.root.add(mesh);

    this.active.push(new ReplayShot(mesh, {
      peerId: data.peerId || 'local',
      stallId: data.stallId,
      kind,
      origin,
      velocity: data.velocity,
      gravityScale: data.gravityScale ?? 1,
    }));
    this.played++;
    return true;
  }

  /** @param {number} dt */
  update(dt) {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const shot = this.active[i];
      shot.elapsed += dt;

      if (!shot.landed) {
        this._fly(shot, dt);
      } else {
        shot.landTimer += dt;
        this._fade(shot, dt);
        if (shot.landTimer >= NET.replayImpactDuration) this._retire(i);
      }
    }
  }

  /** @private */
  _fly(shot, dt) {
    arcPosition(shot.origin, shot.velocity, shot.gravityScale, shot.elapsed, this._scratch);
    shot.mesh.position.copy(this._scratch);
    shot.mesh.rotation.x += shot._spin.x * dt;
    shot.mesh.rotation.y += shot._spin.y * dt;
    shot.mesh.rotation.z += shot._spin.z * dt;

    if (this._scratch.y <= NET.replayImpactY || shot.elapsed >= NET.replayLifetime) {
      shot.landed = true;
      shot.landTimer = 0;
      shot.mesh.visible = false;
      this._impact(shot);
    }
  }

  /**
   * The canned landing: one expanding puff plus a handful of debris chips on
   * ballistic paths of their own. Purely decorative, and deliberately short —
   * it has to sell "something happened over there" without pulling the eye off
   * the player's own lane.
   * @private
   */
  _impact(shot) {
    ensureEffectGeometry();
    const at = shot.mesh.position;

    const puff = new THREE.Mesh(puffGeo, new THREE.MeshBasicMaterial({
      color: 0xd9d2c4, transparent: true, opacity: 0.55, depthWrite: false,
    }));
    puff.position.copy(at);
    puff.userData.puff = true;
    this.root.add(puff);
    shot.effects.push(puff);

    for (let i = 0; i < NET.replayDebrisCount; i++) {
      const angle = (i / NET.replayDebrisCount) * Math.PI * 2;
      const chip = new THREE.Mesh(debrisGeo, new THREE.MeshBasicMaterial({
        color: 0xb9a689, transparent: true, opacity: 0.9, depthWrite: false,
      }));
      chip.position.copy(at);
      chip.userData.velocity = new THREE.Vector3(
        Math.cos(angle) * 1.4, 1.6 + (i % 2) * 0.5, Math.sin(angle) * 1.4,
      );
      this.root.add(chip);
      shot.effects.push(chip);
    }
  }

  /** @private */
  _fade(shot, dt) {
    const k = Math.min(shot.landTimer / NET.replayImpactDuration, 1);
    const fade = 1 - k;

    for (const fx of shot.effects) {
      fx.material.opacity = (fx.userData.puff ? 0.55 : 0.9) * fade;
      if (fx.userData.puff) {
        fx.scale.setScalar(1 + k * 2.2);
        continue;
      }
      const v = fx.userData.velocity;
      fx.position.addScaledVector(v, dt);
      v.y -= DEFAULT_GRAVITY * dt;
      if (fx.position.y < 0.03) fx.position.y = 0.03;
      fx.rotation.x += dt * 6;
      fx.rotation.z += dt * 4;
    }
  }

  /** @private */
  _retire(index) {
    const shot = this.active[index];
    if (!shot) return;
    for (const fx of shot.effects) {
      fx.removeFromParent();
      fx.material.dispose();
    }
    shot.effects.length = 0;
    shot.mesh.removeFromParent();
    this._release(shot.kind, shot.mesh);
    this.active.splice(index, 1);
  }

  /** @private */
  _acquire(kind) {
    const pool = this.pool.get(kind);
    if (pool && pool.length > 0) {
      const mesh = pool.pop();
      mesh.rotation.set(0, 0, 0);
      return mesh;
    }
    const mesh = makeProjectile(kind);
    mesh.name = `replay-${kind}`;
    // Slightly ghosted so a neighbour's ball never reads as one you could catch.
    mesh.traverse((child) => {
      if (!child.isMesh) return;
      child.castShadow = false;
      child.receiveShadow = false;
      child.material = child.material.clone();
      child.material.transparent = true;
      child.material.opacity = 0.78;
    });
    return mesh;
  }

  /** @private */
  _release(kind, mesh) {
    mesh.visible = false;
    let pool = this.pool.get(kind);
    if (!pool) {
      pool = [];
      this.pool.set(kind, pool);
    }
    if (pool.length < NET.maxReplayMeshes) pool.push(mesh);
  }

  /** @returns {number} Shots currently on screen. */
  activeCount() {
    return this.active.length;
  }

  /** @returns {object} Test-facing snapshot. */
  state() {
    return {
      installed: Boolean(this.root),
      active: this.active.length,
      played: this.played,
      pooled: [...this.pool.values()].reduce((n, p) => n + p.length, 0),
      // The assertion that matters: replays never become simulation.
      bodies: 0,
      shots: this.active.map((s) => ({
        peerId: s.peerId,
        stallId: s.stallId,
        kind: s.kind,
        elapsed: Math.round(s.elapsed * 100) / 100,
        landed: s.landed,
        position: s.mesh.position.toArray().map((n) => Math.round(n * 100) / 100),
      })),
    };
  }

  /** Drop everything in flight — called on stall exit and disconnect. */
  clear() {
    for (let i = this.active.length - 1; i >= 0; i--) this._retire(i);
  }

  dispose() {
    this.clear();
    for (const pool of this.pool.values()) {
      for (const mesh of pool) mesh.removeFromParent();
    }
    this.pool.clear();
    this.root?.removeFromParent();
    this.root = null;
  }
}

/** The one replay system. */
export const replaySystem = new ReplaySystem();
